import type { PassTier } from '../../data/placesIndex'
import { getSupabaseOrThrow } from '../supabaseClient'
import {
  FULL_KWOTA,
  FULL_REGULAR_KWOTA,
  MINI_KWOTA,
  MINI_REGULAR_KWOTA,
  isPromoKwota,
} from '../promo/launchPromo'

export type PaidRegistrationInput = {
  email: string
  password: string
  full_name: string
  origin?: string
  heard_about_us?: string
  marketing_consent?: boolean
  gift_count?: number
  gift_emails?: string[]
  pass_tier?: PassTier
  /** Allowlista w workerze (np. newsletter10 = −10%). */
  promo_code?: string
}

export type PaidRegistrationCheckoutResult =
  | { payUrl: string; exchangeToken: string; free?: false }
  | { exchangeToken: string; free: true }

type SessionTokensResponse = {
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: string
  refresh_token_expires_in?: number
}

export type PassTierPricing = {
  kwota: string
  regularKwota?: string
  promoActive?: boolean
}

export type HotpayPricingResponse = {
  mini: PassTierPricing
  full: PassTierPricing
  /** @deprecated Użyj mini.kwota */
  kwota?: string
  regularKwota?: string
  promoActive?: boolean
}

export type UpgradeCheckoutResult = {
  url: string
  diffKwota: string
  fullKwota: string
}

/** Domyślny Worker HotPay w produkcji (nadpisz przez VITE_HOTPAY_WORKER_URL w Cloudflare Pages). */
const DEFAULT_PRODUCTION_HOTPAY_WORKER_URL =
  'https://pds-hotpay-init.z-kowalska-2000.workers.dev'

/**
 * W dev domyślnie proxy Vite → `http://127.0.0.1:8787` (patrz `vite.config.ts`), żeby uniknąć „Failed to fetch” przy stronie przez HTTPS/tunel.
 * Możesz nadpisać: `VITE_HOTPAY_WORKER_URL=http://127.0.0.1:8787`.
 * Produkcja: VITE_HOTPAY_WORKER_URL albo DEFAULT_PRODUCTION_HOTPAY_WORKER_URL poniżej.
 */
export function getHotpayWorkerBaseUrl(): string {
  const fromEnv = (import.meta.env.VITE_HOTPAY_WORKER_URL as string | undefined)?.trim().replace(/\/$/, '')
  if (fromEnv) return fromEnv
  if (import.meta.env.DEV) return '/api/hotpay-worker'
  return DEFAULT_PRODUCTION_HOTPAY_WORKER_URL
}

function parseJsonPayload(raw: string): {
  url?: string
  exchangeToken?: string
  free?: boolean
  error?: string
} | null {
  if (!raw.trim()) return null
  try {
    return JSON.parse(raw) as {
      url?: string
      exchangeToken?: string
      free?: boolean
      error?: string
    }
  } catch {
    return null
  }
}

/** Zapisuje token sesji i przekierowuje po checkout (płatny lub darmowy Mini). */
export function redirectAfterRegistrationCheckout(
  result: PaidRegistrationCheckoutResult,
  email: string,
): void {
  sessionStorage.setItem('pds_reg_email', email)
  sessionStorage.setItem('pds_exchange_token', result.exchangeToken)
  if (result.free) {
    window.location.assign('/login?payment=success&mode=register')
    return
  }
  window.location.assign(result.payUrl)
}

function tierPricingFromKwota(
  kwota: string,
  regularKwota: string,
  promoActive?: boolean,
): PassTierPricing {
  return {
    kwota,
    regularKwota,
    promoActive: promoActive === true || isPromoKwota(kwota, regularKwota),
  }
}

function normalizeTierPricing(raw: unknown, fallbackRegular: string): PassTierPricing | null {
  if (raw && typeof raw === 'object' && 'kwota' in raw) {
    const row = raw as PassTierPricing
    const kwota = typeof row.kwota === 'string' ? row.kwota.trim() : ''
    if (kwota) {
      const regularKwota =
        typeof row.regularKwota === 'string' ? row.regularKwota.trim() : fallbackRegular
      return tierPricingFromKwota(kwota, regularKwota, row.promoActive)
    }
  }
  return null
}

function resolveTierPricing(
  tier: PassTierPricing | null,
  payload: HotpayPricingResponse | null,
  fallbackRegular: string,
  defaultKwota: string,
): PassTierPricing {
  if (tier) return tier

  const legacyKwota = typeof payload?.kwota === 'string' ? payload.kwota.trim() : ''
  if (legacyKwota) {
    return tierPricingFromKwota(legacyKwota, fallbackRegular, payload?.promoActive)
  }

  return tierPricingFromKwota(defaultKwota, fallbackRegular)
}

/**
 * Email + hasło → HotPay INIT. Zwraca link do płatności + token do jednorazowego `completePaidRegistrationSession` po powrocie.
 */
export async function startPaidRegistrationCheckout(
  input: PaidRegistrationInput,
): Promise<PaidRegistrationCheckoutResult> {
  const url = `${getHotpayWorkerBaseUrl().replace(/\/$/, '')}/`
  let res: Response
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        email: input.email.trim().toLowerCase(),
        password: input.password,
        full_name: input.full_name.trim(),
        ...(input.origin?.trim() ? { origin: input.origin.trim() } : {}),
        ...(input.heard_about_us?.trim() ? { heard_about_us: input.heard_about_us.trim() } : {}),
        ...(input.marketing_consent ? { marketing_consent: true } : {}),
        ...(typeof input.gift_count === 'number' ? { gift_count: input.gift_count } : {}),
        ...(Array.isArray(input.gift_emails) ? { gift_emails: input.gift_emails } : {}),
        pass_tier: input.pass_tier === 'mini' ? 'mini' : 'full',
        ...(input.promo_code?.trim()
          ? { promo_code: input.promo_code.trim().toLowerCase() }
          : {}),
      }),
    })
  } catch {
    const dev = import.meta.env.DEV
    throw new Error(
      dev
        ? 'Brak połączenia z serwerem płatności. Uruchom w cloudflare/hotpay-init komendę: npx wrangler dev (port 8787). Nie ustawiaj VITE_HOTPAY_WORKER_URL na http://127.0.0.1 przy stronie przez HTTPS , Vite używa proxy /api/hotpay-worker.'
        : 'Brak połączenia z serwerem płatności. Sprawdź internet oraz czy przy buildzie ustawione jest poprawne VITE_HOTPAY_WORKER_URL (adres HTTPS workera Cloudflare).',
    )
  }

  const raw = await res.text().catch(() => '')
  const payload = parseJsonPayload(raw)
  if (!payload) {
    throw new Error(
      `Serwer zwrócił odpowiedź bez poprawnego JSON (HTTP ${res.status}). ` +
        'Sprawdź, czy adres workera jest poprawny: zmienna VITE_HOTPAY_WORKER_URL (budując frontend) ma wskazywać na Cloudflare Workera hotpay-init, nie na adres strony WWW.',
    )
  }

  if (!res.ok) {
    throw new Error(payload?.error || `Błąd serwera (${res.status}).`)
  }

  if (payload.error) throw new Error(payload.error)

  const exchangeToken = typeof payload.exchangeToken === 'string' ? payload.exchangeToken.trim() : ''
  if (!exchangeToken) throw new Error('Brak tokenu sesji w odpowiedzi serwera (wdróż nowszą wersję workera).')

  if (payload.free === true) {
    return { free: true, exchangeToken }
  }

  const payUrl = typeof payload.url === 'string' ? payload.url.trim() : ''
  if (!payUrl) throw new Error('Brak adresu płatności w odpowiedzi serwera.')

  return { payUrl, exchangeToken, free: false }
}

/**
 * Dopłata Mini → Full dla zalogowanego użytkownika.
 */
export async function startPassUpgradeCheckout(): Promise<UpgradeCheckoutResult> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase.auth.getSession()
  if (error) throw new Error(error.message)
  const accessToken = data.session?.access_token
  if (!accessToken) throw new Error('Zaloguj się, aby dokupić pełny Paszport.')

  const url = `${getHotpayWorkerBaseUrl().replace(/\/$/, '')}/upgrade-checkout`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  const raw = await res.text().catch(() => '')
  let payload: UpgradeCheckoutResult & { error?: string } | null = null
  if (raw.trim()) {
    try {
      payload = JSON.parse(raw) as UpgradeCheckoutResult & { error?: string }
    } catch {
      payload = null
    }
  }
  if (!res.ok || !payload || payload.error) {
    throw new Error(payload?.error || `Błąd dopłaty (${res.status}).`)
  }
  if (!payload.url) throw new Error('Brak adresu płatności dopłaty.')
  return payload
}

/**
 * Wymienia email + token z `startPaidRegistrationCheckout` na tokeny Supabase (po opłaceniu i webhooku).
 */
export async function completePaidRegistrationSession(input: {
  email: string
  exchangeToken: string
}): Promise<SessionTokensResponse> {
  const base = getHotpayWorkerBaseUrl().replace(/\/$/, '')
  const url = `${base}/session-after-payment`
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      email: input.email.trim().toLowerCase(),
      exchangeToken: input.exchangeToken.trim(),
    }),
  })
  const raw = await res.text().catch(() => '')
  let payload: SessionTokensResponse & { error?: string; retry?: boolean } | null = null
  if (raw.trim()) {
    try {
      payload = JSON.parse(raw) as SessionTokensResponse & { error?: string; retry?: boolean }
    } catch {
      payload = null
    }
  }
  if (res.status === 503 && payload?.retry) {
    throw new Error('PENDING_ACCOUNT')
  }
  if (!res.ok || !payload || (payload as { error?: string }).error) {
    const err = (payload as { error?: string } | null)?.error
    throw new Error(err || `Błąd sesji (${res.status}).`)
  }
  if (!payload.access_token || !payload.refresh_token) {
    throw new Error('Niepełna odpowiedź serwera (brak tokenów).')
  }
  return payload
}

let cachedPricing: HotpayPricingResponse | null = null
let pricingInflight: Promise<HotpayPricingResponse> | null = null

/** Cennik Mini + Full z Cloudflare Workera. */
export async function fetchHotpayPricing(): Promise<HotpayPricingResponse> {
  if (cachedPricing) return cachedPricing
  if (pricingInflight) return pricingInflight

  pricingInflight = (async () => {
    const url = `${getHotpayWorkerBaseUrl().replace(/\/$/, '')}/pricing`
    let res: Response
    try {
      res = await fetch(url, { headers: { Accept: 'application/json' } })
    } catch {
      throw new Error(
        import.meta.env.DEV
          ? 'Brak połączenia z serwerem płatności (GET /pricing). Uruchom wrangler dev w cloudflare/hotpay-init.'
          : 'Brak połączenia z serwerem płatności.',
      )
    }
    const raw = await res.text().catch(() => '')
    let payload: HotpayPricingResponse | null = null
    if (raw.trim()) {
      try {
        payload = JSON.parse(raw) as HotpayPricingResponse
      } catch {
        payload = null
      }
    }
    if (!res.ok) {
      const err =
        payload && typeof (payload as { error?: string }).error === 'string'
          ? (payload as { error?: string }).error
          : null
      throw new Error(err || `Błąd odczytu ceny (${res.status}).`)
    }

    const mini = resolveTierPricing(
      normalizeTierPricing(payload?.mini, MINI_REGULAR_KWOTA),
      payload,
      MINI_REGULAR_KWOTA,
      MINI_KWOTA,
    )
    const full = resolveTierPricing(
      normalizeTierPricing(payload?.full, FULL_REGULAR_KWOTA),
      payload,
      FULL_REGULAR_KWOTA,
      FULL_KWOTA,
    )

    const pricing: HotpayPricingResponse = {
      mini,
      full,
      kwota: mini.kwota,
      regularKwota: mini.regularKwota,
      promoActive: mini.promoActive,
    }
    cachedPricing = pricing
    return pricing
  })()

  try {
    return await pricingInflight
  } finally {
    pricingInflight = null
  }
}

export async function fetchHotpayKwota(tier: PassTier = 'full'): Promise<string> {
  const pricing = await fetchHotpayPricing()
  return tier === 'full' ? pricing.full.kwota : pricing.mini.kwota
}

export function pricingForTier(pricing: HotpayPricingResponse, tier: PassTier): PassTierPricing {
  return tier === 'full' ? pricing.full : pricing.mini
}
